import React, { Component } from "react";
import {
  getCouponList,
  toggleCouponStatus,
  deleteCoupon,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Btn, ChipTabs, BarChart, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Campaigns — screen E3·15 of the console redesign (web-coupons.html).
 *
 * A coupon is a thing you launch, pause and retire, not a row in a grid, so
 * the design gives each one a card carrying the two facts that decide what to
 * do next: whether it is running, and how much of it has been used. The table
 * this replaces made you read nine columns to learn the same thing.
 *
 * Re-skin only. Every request, filter param and handler below is the one the
 * previous MDBDataTable screen used.
 */

const CSS = `
.coupon-card{background:#fff;border:1px solid ${C.tint};border-radius:18px;padding:20px;
  display:flex;gap:16px;transition:box-shadow .15s,border-color .15s}
.coupon-card:hover{border-color:${C.sand};box-shadow:0 8px 22px rgba(61,40,23,.09)}
.coupon-card.is-idle{background:${C.surface};border:1px dashed #dfcdb2}
.coupon-menu{border:0;background:transparent;color:#bfae99;cursor:pointer;padding:0;line-height:1}
.coupon-menu:hover{color:${C.brown}}
.coupon-menu-item{display:flex;align-items:center;gap:10px;width:100%;border:0;background:transparent;
  padding:9px 14px;font-size:14px;color:${C.brownDeep};font-family:inherit;text-align:left;cursor:pointer}
.coupon-menu-item:hover{background:${C.surface}}
.coupon-menu-item.is-danger{color:${C.red}}
.coupon-select{height:42px;padding:0 34px 0 16px;border-radius:21px;background:#fff;
  border:1px solid ${C.border};color:${C.taupe};font-size:14px;font-family:inherit;cursor:pointer;
  -webkit-appearance:none;appearance:none;
  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a3917c'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 10px center;background-size:18px}
.coupon-select:focus{outline:none;border-color:${C.sand}}
`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

/** LIVE / SCHEDULED / EXPIRED / PAUSED, from the dates the API already sends. */
const lifecycle = (c) => {
  const now = new Date();
  if (!c.isActive) return { key: "paused", label: "PAUSED", bg: C.tint, fg: C.taupe };
  if (c.expiryDate && now > new Date(c.expiryDate))
    return { key: "expired", label: "EXPIRED", bg: "#f3ece2", fg: C.taupe };
  if (c.startDate && now < new Date(c.startDate))
    return { key: "scheduled", label: "SCHEDULED", bg: "#f3ece2", fg: C.taupe };
  return { key: "live", label: "LIVE", bg: "#e6f4e6", fg: "#2f7d33" };
};

const discountLabel = (c) =>
  c.discountType === "percentage" ? `${c.discountValue}% off` : `$${c.discountValue} off`;

/** One-line summary, preferring the copy an admin actually wrote. */
const blurb = (c) => {
  if (c.description) return c.description;
  const bits = [discountLabel(c)];
  if (c.scopeLevel && c.scopeLevel !== "all") bits.push(`${c.scopeLevel} only`);
  if (c.minOrderValue > 0) bits.push(`min $${c.minOrderValue}`);
  if (c.uptoAmount > 0 && c.discountType === "percentage") bits.push(`max $${c.uptoAmount} off`);
  return bits.join(" · ");
};

class CouponList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      statusFilter: "",
      typeFilter: "",
      userTypeFilter: "",
      openMenuId: null,
      // Modal states
      showToggleModal: false,
      showDeleteModal: false,
      selectedCouponId: null,
      selectedCouponStatus: null,
      actionLoading: false,
    };
  }

  componentDidMount() {
    this.getAllCoupons();
  }

  getAllCoupons = () => {
    const { statusFilter, typeFilter, userTypeFilter } = this.state;
    const params = {};
    if (statusFilter) params.status = statusFilter;
    // Pass discount type filter correctly
    if (typeFilter && typeFilter !== "") params.discountType = typeFilter;
    // Only pass userTypeFilter if not empty and not the default "All Users" option
    if (userTypeFilter && userTypeFilter !== "") params.userType = userTypeFilter;

    getCouponList(params)
      .then((result) => {
        if (result.status === 200) {
          this.setState({ isLoaded: true, items: result.data });
        } else {
          toast.error(result.message || "Failed to fetch coupons");
        }
      })
      .catch(() => toast.error("Error fetching coupons"));
  };

  // Modal toggle handlers
  toggleToggleModal = () => this.setState({ showToggleModal: !this.state.showToggleModal });
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleToggleStatus = (id, currentStatus) => {
    this.setState({
      selectedCouponId: id,
      selectedCouponStatus: currentStatus,
      showToggleModal: true,
      openMenuId: null,
    });
  };

  confirmToggleStatus = () => {
    const { selectedCouponId, selectedCouponStatus } = this.state;
    const action = selectedCouponStatus ? "deactivate" : "activate";
    this.setState({ actionLoading: true });
    toggleCouponStatus(selectedCouponId)
      .then((response) => {
        if (response.status === 200) {
          toast.success(`Coupon ${action}d successfully`);
          this.getAllCoupons();
        } else {
          toast.error(response.message || `Failed to ${action} coupon`);
        }
      })
      .catch(() => toast.error(`Error ${action}ing coupon`))
      .finally(() => {
        this.setState({
          actionLoading: false,
          showToggleModal: false,
          selectedCouponId: null,
          selectedCouponStatus: null,
        });
      });
  };

  handleDelete = (id) => {
    this.setState({ selectedCouponId: id, showDeleteModal: true, openMenuId: null });
  };

  confirmDelete = () => {
    const { selectedCouponId } = this.state;
    this.setState({ actionLoading: true });
    deleteCoupon(selectedCouponId)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Coupon deleted successfully");
          this.getAllCoupons();
        } else {
          toast.error(response.message || "Failed to delete coupon");
        }
      })
      .catch(() => toast.error("Error deleting coupon"))
      .finally(() => {
        this.setState({ actionLoading: false, showDeleteModal: false, selectedCouponId: null });
      });
  };

  handleFilterChange = (e) => {
    this.setState({ [e.target.name]: e.target.value }, () => {
      this.getAllCoupons();
    });
  };

  setStatusFilter = (v) => {
    this.setState({ statusFilter: v }, this.getAllCoupons);
  };

  selectedCoupon = () =>
    this.state.items.find((c) => c._id === this.state.selectedCouponId) || null;

  /**
   * Per-card overflow menu. A transparent full-screen layer closes it, which is
   * how the rest of the app's popovers behave and avoids a document listener
   * in a class component.
   */
  renderMenu(coupon) {
    if (!canWrite()) return null;
    const open = this.state.openMenuId === coupon._id;
    return (
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className="coupon-menu"
          aria-label={`Actions for ${coupon.code}`}
          onClick={() => this.setState({ openMenuId: open ? null : coupon._id })}
        >
          <span className="ms" style={{ fontSize: 20 }}>more_horiz</span>
        </button>
        {open && (
          <React.Fragment>
            <div
              onClick={() => this.setState({ openMenuId: null })}
              style={{ position: "fixed", inset: 0, zIndex: 20 }}
            />
            <div
              style={{
                position: "absolute", top: 26, right: 0, zIndex: 21, minWidth: 178,
                background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14,
                boxShadow: "0 12px 30px rgba(61,40,23,.16)", padding: "6px 0", overflow: "hidden",
              }}
            >
              <button
                type="button"
                className="coupon-menu-item"
                onClick={() => this.props.history.push(`/coupon/edit/${coupon._id}`)}
              >
                <span className="ms" style={{ fontSize: 18 }}>edit</span>
                Edit coupon
              </button>
              <button
                type="button"
                className="coupon-menu-item"
                onClick={() => this.handleToggleStatus(coupon._id, coupon.isActive)}
              >
                <span className="ms" style={{ fontSize: 18 }}>
                  {coupon.isActive ? "pause" : "play_arrow"}
                </span>
                {coupon.isActive ? "Pause" : "Activate"}
              </button>
              <button
                type="button"
                className="coupon-menu-item is-danger"
                onClick={() => this.handleDelete(coupon._id)}
              >
                <span className="ms" style={{ fontSize: 18 }}>delete</span>
                Delete
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }

  renderCard(coupon) {
    const state = lifecycle(coupon);
    const idle = state.key !== "live";
    const used = coupon.currentUsage || 0;
    const unlimited = coupon.usageLimit === 0 || coupon.usageLimit == null;
    const write = canWrite();

    return (
      <div key={coupon._id} className={`coupon-card${idle ? " is-idle" : ""}`}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 17, fontWeight: 500, color: idle ? C.taupe : C.ink }}>
              {coupon.code}
            </span>
            <span
              style={{
                padding: "4px 9px", borderRadius: 9, fontSize: 12, fontWeight: 700,
                background: state.bg, color: state.fg,
              }}
            >
              {state.label}
            </span>
          </div>

          <div
            style={{
              marginTop: 6, fontSize: 13, color: idle ? C.taupeLight : C.taupe,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
            title={blurb(coupon)}
          >
            {blurb(coupon)}
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 18 }}>
            <div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>Redeemed</div>
              <div style={{ marginTop: 3, fontSize: 18, color: C.ink }}>{used}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>Usage cap</div>
              <div style={{ marginTop: 3, fontSize: 18, color: C.ink }}>
                {unlimited ? "∞" : coupon.usageLimit}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>Per guest</div>
              <div style={{ marginTop: 3, fontSize: 18, color: C.ink }}>
                {coupon.usagePerUser === 0 ? "∞" : coupon.usagePerUser}
              </div>
            </div>
          </div>

          {state.key === "paused" && write && (
            <button
              type="button"
              onClick={() => this.handleToggleStatus(coupon._id, coupon.isActive)}
              style={{
                marginTop: 14, border: 0, background: "transparent", padding: 0,
                fontSize: 14, color: C.brown, fontWeight: 500, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Put it live →
            </button>
          )}
        </div>

        <div
          style={{
            width: 82, display: "flex", flexDirection: "column",
            alignItems: "flex-end", justifyContent: "space-between",
          }}
        >
          {this.renderMenu(coupon)}
          <span style={{ fontSize: 12, color: C.taupeLight, textAlign: "right" }}>
            ends
            <br />
            {fmtDate(coupon.expiryDate)}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const { items, isLoaded, statusFilter, typeFilter, userTypeFilter } = this.state;
    const write = canWrite();
    const selected = this.selectedCoupon();

    const counts = items.reduce((acc, c) => {
      const k = lifecycle(c).key;
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});

    // Redemptions per campaign, straight from currentUsage. The handoff draws a
    // 14-day time series here; the coupon API returns no per-day history and one
    // is not invented — this charts what it does return.
    const chart = items
      .filter((c) => (c.currentUsage || 0) > 0)
      .sort((a, b) => (b.currentUsage || 0) - (a.currentUsage || 0))
      .slice(0, 12)
      .map((c) => ({ label: c.code, value: c.currentUsage || 0 }));

    return (
      <ConsolePage
        title="Campaigns"
        subtitle={
          isLoaded
            ? `${items.length} coupon${items.length === 1 ? "" : "s"} · ${counts.live || 0} live`
            : "Loading campaigns…"
        }
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="image" onClick={() => this.props.history.push("/banner")}>
              Banner slots
            </Btn>
            {write && (
              <Btn icon="add" onClick={() => this.props.history.push("/coupon/create")}>
                New coupon
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ChipTabs
              value={statusFilter}
              onChange={this.setStatusFilter}
              options={[
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "expired", label: "Expired" },
              ]}
            />
            <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
              <select
                className="coupon-select"
                name="typeFilter"
                value={typeFilter}
                onChange={this.handleFilterChange}
                aria-label="Discount type"
              >
                <option value="">All discount types</option>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed amount</option>
              </select>
              <select
                className="coupon-select"
                name="userTypeFilter"
                value={userTypeFilter}
                onChange={this.handleFilterChange}
                aria-label="Guest type"
              >
                <option value="">All guests</option>
                <option value="all">All Users</option>
                <option value="first_time">First time only</option>
                <option value="loyalty">Loyalty users</option>
              </select>
            </div>
          </div>

          {!isLoaded ? (
            <div style={{ padding: "60px 0", textAlign: "center", color: C.taupeLight }}>
              Loading campaigns…
            </div>
          ) : items.length === 0 ? (
            <Panel>
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <span className="ms" style={{ fontSize: 34, color: C.sand }}>sell</span>
                <div style={{ marginTop: 12, fontSize: 16, color: C.ink }}>No campaigns yet</div>
                <div style={{ marginTop: 6, fontSize: 14, color: C.taupe }}>
                  {statusFilter || typeFilter || userTypeFilter
                    ? "Nothing matches these filters."
                    : "A coupon appears in the guest app as soon as it goes live."}
                </div>
                {write && !statusFilter && !typeFilter && !userTypeFilter && (
                  <div style={{ marginTop: 18, display: "flex", justifyContent: "center" }}>
                    <Btn icon="add" onClick={() => this.props.history.push("/coupon/create")}>
                      Create the first coupon
                    </Btn>
                  </div>
                )}
              </div>
            </Panel>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
                gap: 16,
              }}
            >
              {items.map((c) => this.renderCard(c))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 18, alignItems: "start" }}>
            <Panel title="Redemptions by campaign" subtitle="Total uses to date — the API keeps no daily history">
              <div style={{ marginTop: 18 }}>
                <BarChart data={chart} height={190} empty="No coupon has been redeemed yet" />
              </div>
            </Panel>

            <Panel title="Campaign mix">
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  ["Live now", counts.live || 0, "#2f7d33"],
                  ["Scheduled", counts.scheduled || 0, C.ink],
                  ["Paused", counts.paused || 0, C.ink],
                  ["Expired", counts.expired || 0, C.ink],
                ].map(([label, value, colour]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                    <span>{label}</span>
                    <span style={{ color: colour, fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
                <div style={{ marginTop: 4, paddingTop: 14, borderTop: `1px solid ${C.panel}`, display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                  <span>Total redemptions</span>
                  <span style={{ color: C.ink, fontWeight: 500 }}>
                    {items.reduce((n, c) => n + (c.currentUsage || 0), 0)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => this.props.history.push("/banner")}
                style={{
                  marginTop: 20, border: 0, background: "transparent", padding: 0,
                  fontSize: 14, fontWeight: 500, color: C.brown, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Manage app banners →
              </button>
            </Panel>
          </div>
        </div>

        {/* Toggle Status Confirmation */}
        <ConfirmDialog
          isOpen={this.state.showToggleModal}
          toggle={this.toggleToggleModal}
          onConfirm={this.confirmToggleStatus}
          tone="neutral"
          icon={this.state.selectedCouponStatus ? "pause" : "play_arrow"}
          title={
            this.state.selectedCouponStatus
              ? `Pause ${selected ? selected.code : "this coupon"}?`
              : `Put ${selected ? selected.code : "this coupon"} live?`
          }
          body={
            this.state.selectedCouponStatus
              ? "Guests will stop seeing the code in the app straight away. Past redemptions are untouched, and you can put it back later."
              : "The code becomes usable in the guest app immediately, within its own start and end dates."
          }
          confirmText={this.state.selectedCouponStatus ? "Pause coupon" : "Put it live"}
          loading={this.state.actionLoading}
        />

        {/* Delete Confirmation — screen G3 of the handoff */}
        <ConfirmDialog
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          icon="delete"
          title={`Delete ${selected ? selected.code : "this coupon"}?`}
          body={
            selected && (selected.currentUsage || 0) > 0
              ? `${selected.currentUsage} redemption${selected.currentUsage === 1 ? " has" : "s have"} already been made against this coupon. Deleting removes it from the app but keeps past redemptions in reporting.`
              : "This removes the coupon from the guest app."
          }
          tip="Pausing keeps the code reusable later. Deleting cannot be undone."
          confirmText="Delete"
          altText={selected && selected.isActive ? "Pause instead" : undefined}
          onAlt={
            selected && selected.isActive
              ? () => {
                  this.setState({ showDeleteModal: false });
                  this.handleToggleStatus(selected._id, true);
                }
              : undefined
          }
          loading={this.state.actionLoading}
        />
      </ConsolePage>
    );
  }
}

export default CouponList;
